import { useState } from "react";
import { FolderKanban, Plus, Inbox, Check, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import type { WorkspaceProject } from "@/hooks/useWorkspaceProjects";

interface ProjectSelectorProps {
  projects: WorkspaceProject[];
  activeProjectId: string | null;
  onSelectProject: (id: string | null) => void;
  onOpenManager: () => void;
  loading?: boolean;
  isAdmin?: boolean;
  orphanCount?: number;
}

export function ProjectSelector({
  projects,
  activeProjectId,
  onSelectProject,
  onOpenManager,
  loading = false,
  isAdmin = false,
  orphanCount = 0,
}: ProjectSelectorProps) {
  const [open, setOpen] = useState(false);
  const activeProject = projects.find((p) => p.id === activeProjectId);
  const isInbox = activeProjectId === null;

  const handleSelect = (id: string | null) => {
    onSelectProject(id);
    setOpen(false);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="flex-1 min-w-0 sm:flex-initial h-9" disabled={loading}>
          {isInbox ? (
            <Inbox className="w-4 h-4 mr-1.5 shrink-0" />
          ) : (
            <FolderKanban className="w-4 h-4 mr-1.5 shrink-0" />
          )}
          <span className="truncate max-w-[7.5rem] sm:max-w-[140px]">
            {loading
              ? "Carregando..."
              : isInbox
                ? "Gerais"
                : activeProject?.name ?? "Projeto"}
          </span>
          {activeProject && (
            <div className={cn("w-2 h-2 rounded-full ml-2 hidden sm:block", activeProject.color)} />
          )}
          <ChevronDown className="w-4 h-4 text-muted-foreground ml-1 shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64 bg-popover border shadow-lg z-50">
        <DropdownMenuLabel>Projeto</DropdownMenuLabel>
        <DropdownMenuSeparator />

        {orphanCount > 0 && (
          <DropdownMenuItem
            onClick={() => handleSelect(null)}
            className="flex items-center gap-3 cursor-pointer"
          >
            <Inbox className="w-4 h-4 shrink-0 text-muted-foreground" />
            <span className="flex-1">Gerais</span>
            <span className="text-xs text-muted-foreground tabular-nums">{orphanCount}</span>
            {isInbox && <Check className="w-4 h-4 text-primary" />}
          </DropdownMenuItem>
        )}

        {projects.length === 0 ? (
          <div className="px-2 py-3 text-center text-sm text-muted-foreground">
            Nenhum projeto cadastrado.
          </div>
        ) : (
          projects.map((project) => (
            <DropdownMenuItem
              key={project.id}
              onClick={() => handleSelect(project.id)}
              className="flex items-center gap-3 cursor-pointer"
            >
              <div className={cn("w-3 h-3 rounded-full shrink-0", project.color)} />
              <span className="flex-1 truncate">{project.name}</span>
              {project.id === activeProjectId && <Check className="w-4 h-4 text-primary" />}
            </DropdownMenuItem>
          ))
        )}

        {isAdmin && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => {
                setOpen(false);
                onOpenManager();
              }}
              className="cursor-pointer"
            >
              <Plus className="w-4 h-4 mr-2" />
              Criar / Gerenciar Projetos
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
